import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ProgressService} from '../../../shared/services/progress.service';
import {SnackbarService} from '../../../shared/services/snackbar.service';
import {SettingsDataService} from '../../../shared/services/settings-data.service';
import {Table} from "../../services/models/translation-service.model";
import {DialogService} from "primeng/dynamicdialog";
import {
    IdentificationPopup
} from "../../../shared/components/config-identification-popup/config-identification-popup.component";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {
    ConfigDetailSkeletonComponent
} from "../../../shared/components/config-detail-skeleton/config-detail-skeleton.component";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {debounceTime, distinctUntilChanged} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslationDataService} from "../../services/translation-data.service";
import {RefreshService} from "../../../shared/services/refresh.service";
import {CardModule} from "primeng/card";
import {InputTextModule} from "primeng/inputtext";
import {IconField} from "primeng/iconfield";
import {InputIcon} from "primeng/inputicon";
import {ButtonModule} from "primeng/button";
import {SelectModule} from "primeng/select";
import {TableModule} from "primeng/table";

@Component({
    selector: 'app-translation-detail',
    templateUrl: './translation-detail.component.html',
    styleUrls: ['./translation-detail.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        FormsModule,
        DetailHeaderComponent,
        NgOptimizedImage,
        ConfigDetailSkeletonComponent,
        ConfigDetailComponent,
        DetailBodyComponent,
        CardModule,
        InputTextModule,
        IconField,
        InputIcon,
        ButtonModule,
        SelectModule,
        TableModule,
    ]
})
export class TranslationDetailComponent implements OnInit {
    @Input() navigateBack?: { commands: any[], extras?: NavigationExtras };

    protected readonly progressService = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly settingsDataService = inject(SettingsDataService);
    private readonly router = inject(Router);
    private readonly dialogService = inject(DialogService);
    private readonly translate = inject(TranslateService);
    private readonly config = inject(ConfigurationService).configuration;
    private readonly translationDataService = inject(TranslationDataService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly refreshService = inject(RefreshService);


    protected readonly ConfigRouteEnum = ConfigRouteEnum;

    searchId: string | undefined;
    dataSource: Table[] = [];
    selection: Table[] = [];
    searchInput = new FormControl('');
    selectedLanguage = '';
    logoUrl: string | null = this.config.app.defaultLogo;
    title: string | undefined;

    language = [
        {value: 'lngDE'},
        {value: 'lngEN'},
    ];

    get languageOptions() {
        return [
            {label: this.translate.instant('translation.all'), value: ''},
            ...this.language.map(l => ({label: this.translate.instant('translation.' + l.value), value: l.value})),
        ];
    }

    private initialLoadDone = false;

    ngOnInit() {
        this.subscribeToRoute();
        this.setupSearch();
        this.loadHeaderInfo(this.searchId ?? '');

        this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.searchInput.setValue('');
            this.subscribeToRoute();
        });
    }

    private setupSearch(): void {
        this.searchInput.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(searchTerm => {
            this.loadTranslationsTable(this.searchId ?? '', (searchTerm ?? '').trim().toLowerCase());
        });
    }

    private loadHeaderInfo(appName: string) {
        this.settingsDataService.findAllByApplicationName('ConfigurationServiceUI').subscribe({
            next: (data: any[]) => {
                const settings = data?.[0];
                if (!settings) return;

                try {
                    const appSettings = JSON.parse(settings.applicationSettings);
                    const translation = appSettings.translations?.find((translation: any) => translation.searchName === appName);
                    this.title = translation?.name || appName;
                } catch {
                    this.title = appName;
                }
            }
        });
    }

    private subscribeToRoute() {
        this.activatedRoute.data.subscribe({
            next: data => {
                this.activatedRoute.paramMap.subscribe({
                    next: paramMap => {
                        let searchIdParam = paramMap.get('searchName');
                        if (!searchIdParam) return;

                        this.searchId = searchIdParam;
                        this.initialLoadDone = false;
                        this.loadTranslationsTable(this.searchId);
                    }
                })
            }
        })
    }

    loadTranslationsTable(appName: string, searchTerm: string = ''): void {
        const showSkeleton = !this.initialLoadDone && !searchTerm;
        if (showSkeleton) {
            this.progressService.startLoading();
        }

        const translations = searchTerm
            ? this.translationDataService.searchTranslations(appName, searchTerm)
            : this.settingsDataService.findAllByApplicationName(appName);

        translations.subscribe({
            next: (data: any[]) => {
                this.dataSource = data as Table[];
                this.progressService.stopLoading();
                if (showSkeleton) {
                    this.initialLoadDone = true;
                }
            },
            error: () => {
                this.snackBar.Error(this.translate.instant("snackbar.translation.error"));
                this.progressService.stopLoading();
            }
        });
    }

    get filteredDataSource(): Table[] {
        if (!this.selectedLanguage) return this.dataSource;
        return this.dataSource.filter(row => row.tenantName === this.selectedLanguage);
    }

    addTranslation() {
        this.dialogService.open(IdentificationPopup, {
            header: this.translate.instant('translation.add'),
            modal: true,
            closable: true,
            resizable: false,
            draggable: false,
            width: '32rem',
            breakpoints: {'640px': '90vw'},
            focusOnShow: true,
        })?.onClose.subscribe(identification => {
            if (identification) {
                this.router.navigate([identification], {relativeTo: this.activatedRoute});
            }
        })
    }

    translationEdit(row: Table) {
        this.router.navigate([row.identification], {relativeTo: this.activatedRoute});
    }
}